import { TankQuickFilterType } from '../modules/tank-quick-filters/components/global-tank-filters/tank-quick-filters.component';
/// <reference path="dashboard-resolver.service.ts" />
import { formatNumber } from '@angular/common';
import {
  HttpErrorResponse,
  HttpHeaders
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  throwError,
} from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { Cacheable } from 'ts-cacheable';
import { IInfoPanelDataItem } from 'src/app/components/info-panel/info-panel.component';
import { AppState } from '../app.state';
import { ITankHistoryEstimatedShipmentInfo } from '@interfaces/ITankHistoryEstimatedShipmentInfo';
import { ITankHistoryEstimatedUsageInfo } from '@interfaces/ITankHistoryEstimatedUsageInfo';
import { ITankHistoryInventoryInfo } from '@interfaces/ITankHistoryInventoryInfo';
import {
  KeyDatesChartResponse,
  KeyDatesChartVM,
  KeyDateTypes,
} from '@interfaces/keydateschartresponse';
import { IKeyTankInfo } from '@interfaces/keyTankInfo';
import { TankDetailsPhotoDTO } from '@interfaces/tankDetailsPhotoDto';
import { ITankHistoryUsageInfo } from '@interfaces/tankHistoryUsageInfo';
import { ITankStatus } from '@interfaces/tankStatus';
import { ITankVolumeDataPoint } from '@interfaces/tankVolumeUsageChart';
import { IDateRange } from '../page/tank/components/tank-history-usage/tank-history-usage.component';
import { DSRESTClient } from '../shared/communication-objects/restclient.service';
import { api, part } from '../shared/communication-objects/api-path';
import { selectUserInformation } from '../state/selectors/user-profile.selectors';
import { FileUtilities } from '../utilities/fileUtilities';
import { IAlarmStatus } from '@interfaces/alarmStatus';
import { ApiSuccessfulResponse } from './../shared/models/apiSuccessfulResponse';
import { AppDialogService } from './app-dialog.service';
import { HistoricalDeliveryEfficiencyChartDTO } from './dashboard-widget.service';
import { LogService } from './log.service';
import { ITankAggregatedUsageChartVM } from '@interfaces/tankAggregatedUsageChart';
import { ITankDiagnosticRecord } from '@interfaces/ITankDiagnosticRecord';
import { HttpClient } from '@angular/common/http';

export const ChartColors = {
  yellow: '#FDB933',
  red: '#c33e4b',
  green: '#54af25',
  blue: 'blue',
};

//Tank Filter options for the Tank Page.
export const TankFiltersOptions = [
  {
    filterType: TankQuickFilterType.ActiveTanks,
    name: 'TankFilters.ActiveTanks',
    icon: 'tanks-active.svg',
    value: null, //value: this.getFilterData(TankFilterType.ActiveTanks),
    color: '#0088DD',
  },
  {
    filterType: TankQuickFilterType?.NoRecentUpdates,
    name: 'TankFilters.NoRecentUpdates',
    icon: 'tank-inactive.svg',
    value: null, //value: this.getFilterData(TankFilterType.NoRecentUpdates),
    color: '#818181',
  },
  {
    filterType: TankQuickFilterType?.RecentInstalls,
    name: 'TankFilters.RecentInstalls',
    icon: 'tank-recent-installs.svg',
    value: null, //value: this.getFilterData(TankFilterType.RecentInstalls),
    color: '#00AB4E',
  },
  {
    filterType: TankQuickFilterType?.CriticalLowAlarms,
    name: 'TankFilters.CriticalLowAlarms',
    icon: 'tank-critical-low-alarm.svg',
    value: null, //value: this.getFilterData(TankFilterType.CriticalLowAlarms),
    color: '#FF0000',
  },
  {
    filterType: TankQuickFilterType?.LowAlarms,
    name: 'TankFilters.LowAlarms',
    icon: 'tank-low-alarm.svg',
    value: null, //value: this.getFilterData(TankFilterType.LowAlarms),
    color: '#FDB933',
  },
  {
    filterType: TankQuickFilterType?.LowBattery,
    name: 'TankFilters.LowBattery',
    icon: 'tank-low-battery.svg',
    value: null, //value: this.getFilterData(TankFilterType.LowBattery),
    color: '#DF292B',
  },
  {
    filterType: TankQuickFilterType?.ReorderAlarms,
    name: 'TankFilters.ReorderAlarms',
    icon: 'tank-reorder-alarm.svg',
    value: null, // value: this.getFilterData(TankFilterType.ReorderAlarms),
    color: '#FDB933',
  },
  {
    filterType: TankQuickFilterType?.SafetyStockAlarms,
    name: 'TankFilters.SafetyStockAlarms',
    icon: 'tank-safety-stock-alarm.svg',
    value: null, //value: this.getFilterData(TankFilterType.SafetyStockAlarms),
    color: '#00AB4E',
  },
  {
    filterType: TankQuickFilterType?.LeastEfficientDeliveries,
    name: 'TankFilters.LeastEfficientDeliveries',
    icon: 'tank-low-alarm.svg',
    value: null, //value: this.getFilterData(TankFilterType.SafetyStockAlarms),
    color: '#FDB933',
  },
  {
    filterType: TankQuickFilterType?.NeedsDelivery,
    name: 'TankFilters.NeedsDelivery',
    icon: 'tank-needs-delivery.svg',
    value: null,
    color: '#3695C7',
  },
  {
    filterType: TankQuickFilterType?.ReceivingLocations,
    name: 'TankFilters.ReceivingLocations',
    icon: 'map-tank-pin-gray.svg',
    value: null,
    color: 'gray', //TODO: change this color to something else. Need to talk to Business
  },
];

@Injectable({ providedIn: 'root' })
export class TankService {
  private readonly apiURL = api`Tank`;
  private readonly detailsApiUrl = api`tank-details`;
  private readonly tankNotInScopeMessageKey = 'Unauthorized.TankNotInScope';
  public storage: any; // remove this later
  public storage_temp: any; // remove this later
  public storage_cell: any; // remove this later

  constructor(
    private restClient: DSRESTClient,
    private logger: LogService,
    private store: Store<AppState>,
    private router: Router,
    private _ads: AppDialogService,
    private _translate: TranslateService,
    private _httpClient: HttpClient,
  ) { }

  searchForTanks(payload: any): Observable<any[]> {
    const apiResponse = this.restClient.post(api`${this.apiURL}/search`, payload);
    return apiResponse.pipe(
      tap((response) => this.logger.log('Tank/Search Response', response)),
    );
  }

  getGlobalSearchRowByUniqueId(uniqueId: number): Observable<any> {
    const apiResponse = this.restClient.get(
      api`${this.apiURL}/global-search-row/${uniqueId}`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('Tank/getGlobalSearchRowByUniqueId Response', response),
      ),
    );
  }

  private _tankInfo$ = new BehaviorSubject<IKeyTankInfo>(null); //new ReplaySubject<any>(1);
  private _tankInfo: IKeyTankInfo | null;
  private _uniqueId: number;

  //the cached key tank info as a stream.
  // Emits every time the key tank info is (re)loaded, so pages showing the key tank
  // can react to changes made without navigation (move tank, swap RTU, unit changes).
  readonly keyTankInfo$: Observable<IKeyTankInfo> = this._tankInfo$.asObservable();

  //drop the cached key tank info so the next read re-fetches from the server.
  // Use when the tank changed but the current page is being navigated away from anyway.
  invalidateKeyTankInfo(): void {
    this._uniqueId = null;
    this._tankInfo = null;
  }

  //reload the tank info for the key tank
  // To be called/used when tank information is saved but navigation has not occured.
  // Subscribes internally so existing fire-and-forget callers keep working, and returns
  // the shared result for callers that need to sequence work after the reload completes.
  reloadKeyTankInfo(): Observable<IKeyTankInfo> {
    if (this._uniqueId === null || this._uniqueId === undefined) return of(null);
    const uniqueId = this._uniqueId;
    this.invalidateKeyTankInfo();
    const reload$ = this.getKeyTankInfo(uniqueId).pipe(take(1), shareReplay(1));
    //eager subscribe: the reload must happen even when the caller ignores the result
    reload$.subscribe(() => { });
    return reload$;
  }

  //get and cache the tank info for the key tank
  // return the observable
  getKeyTankInfo(uniqueId: number): Observable<any> {
    return this._tankInfo$.pipe(
      switchMap((tankInfo) => {
        if (tankInfo && this._uniqueId === uniqueId) {
          // If the cached data matches the current ID, use it.
          return of(tankInfo);
        } else {
          // Otherwise, fetch new data from the server and cache it.
          return this.restClient
            .getDirect<IKeyTankInfo>(
              api`${this.apiURL}/getKeyTankInfo/${uniqueId}`,
            )
            .pipe(
              tap((newData) => {
                if (!newData)
                  throw new HttpErrorResponse({ status: 404, statusText: 'Tank not found' });
                this._tankInfo = newData;
                this._uniqueId = uniqueId;
                this._tankInfo$.next(newData);
                this.logger.log('Tank/getKeyTankInfo Response', newData);
              }),
              catchError((error: HttpErrorResponse) => {
                // GetTankForDetails gates every per-tank read on IUserSecurity.CanViewTank and
                // throws UnauthorizedAccessException, which Startup maps to 403. Handled here
                // rather than left to rethrow: TankAccessGuard only wraps the validateCSOIsTankOrg
                // leg, so an error escaping this call aborts the navigation with nothing shown.
                // Same shape as the 404 branch below - clear the cache, tell the user, go home.
                if (error.status === 403) {
                  this._tankInfo = null;
                  this._uniqueId = null;
                  this._tankInfo$.next(null);
                  this.logger.log('Tank/getKeyTankInfo Forbidden', error);
                  this._ads.showErrorMessage(
                    this._translate.instant(this.tankNotInScopeMessageKey),
                  );
                  this.router.navigate(['/dashboard']);
                  return of(null);
                }

                if (error.status === 404) {
                  this._tankInfo = null;
                  this._uniqueId = null;
                  this._tankInfo$.next(null);
                  this.logger.log('Tank/getKeyTankInfo Error', error);
                  //snackbar..
                  this._ads.showErrorMessage(
                    this._translate.instant('General.TankNotFound'),
                  );
                  //redirect to the dashboard
                  this.router.navigate(['/dashboard']);
                  return of(null);
                }
                return throwError(() => error);
              }),
            );
        }
      }),
    );
  }

  getKeyTankInfo$(): Observable<IKeyTankInfo> {
    return this._tankInfo$;
  }

  getKeyTankInfoDirectFromCache() {
    return this._tankInfo;
  }
  getNextOrPrevTank(params: any): Observable<any> {
    let apiResponse = this.restClient.post(
      api`${this.apiURL}/getNextOrPrevTank`,
      params,
    );
    return apiResponse.pipe(
      tap((response) => {
        this.logger.log('Tank/getNextOrPrevTank Response', response);
      }),
    );
  }

  saveInventoryRecord(payload: any): Observable<any> {
    let apiResponse = this.restClient.post(
      api`${this.apiURL}/SaveInventoryRecord`,
      payload,
    );
    return apiResponse.pipe(
      tap((response) => {
        this.logger.log('Tank/SaveInventoryRecord Response', response);
      }),
    );
  }

  getAnomalyNotificationSeverityRollup(params: any): Observable<any> {
    const url = api`${this.apiURL}/GetAnomalyNotificationSeverityRollup/${part(params?.orgId)}`;
    return this.restClient.getDirect(url).pipe(
      tap((response) => {
        this.logger.log(url, response);
      }),
    );
  }

  //Get Details Widgets for the Tank Details Page
  getAlarmStatus(
    uniqueId: number,
  ): Observable<ApiSuccessfulResponse<IAlarmStatus>> {
    return this.restClient
      .get<IAlarmStatus>(api`${this.detailsApiUrl}/alarms/${uniqueId}`)
      .pipe(
        tap((data) => this.logger.log('getAlarmStatus API Results: ', data)),
      );
  }

  getStatus(
    uniqueId: number,
  ): Observable<ApiSuccessfulResponse<ITankStatus[]>> {
    return this.restClient.get<ITankStatus[]>(
      api`${this.detailsApiUrl}/status/${uniqueId}`,
    );
  }

  getPhotos(uniqueId: number): Observable<TankDetailsPhotoDTO[]> {
    return this.restClient.getAsModel<TankDetailsPhotoDTO[]>(
      api`${this.detailsApiUrl}/photos/${uniqueId}`,
    );
  }

  getPhotosWithLatLong(uniqueId: number): Observable<TankDetailsPhotoDTO[]> {
    return this.restClient.getAsModel<TankDetailsPhotoDTO[]>(
      api`${this.detailsApiUrl}/photos-with-lat-long/${uniqueId}`,
    );
  }

  getDeliveryEfficiencyHistory(
    uniqueId: number,
  ): Observable<HistoricalDeliveryEfficiencyChartDTO> {
    return this.restClient.getAsModel<HistoricalDeliveryEfficiencyChartDTO>(
      api`${this.detailsApiUrl}/delivery-efficiency-history/${uniqueId}`,
    );
  }

  getDiagnostics(
    uniqueId: number,
    startDate: Date,
    endDate: Date,
  ): Observable<ITankDiagnosticRecord[]> {
    const pad = (value: number) => (value < 10 ? `0${value}` : `${value}`);
    const start =
      `${startDate.getFullYear()}-` +
      `${pad(startDate.getMonth() + 1)}-` +
      `${pad(startDate.getDate())}T` +
      `${pad(startDate.getHours())}:` +
      `${pad(startDate.getMinutes())}:` +
      `${pad(startDate.getSeconds())}`;
    const end =
      `${endDate.getFullYear()}-` +
      `${pad(endDate.getMonth() + 1)}-` +
      `${pad(endDate.getDate())}T` +
      `${pad(endDate.getHours())}:` +
      `${pad(endDate.getMinutes())}:` +
      `${pad(endDate.getSeconds())}`;
    return this.restClient.getAsModel<ITankDiagnosticRecord[]>(
      api`${this.detailsApiUrl}/diagnostics/${uniqueId}?startDate=${part(start)}&endDate=${part(end)}`,
    );
  }

  getInformation(uniqueId: number): Observable<any> {
    //this.testMobileAppTankInstallerAPIs(); // remove this later
    return this.restClient.get<any>(
      api`${this.detailsApiUrl}/information/${uniqueId}`,
    );
  }
  getMap(uniqueId: number): Observable<any> {
    return this.restClient.get<any>(api`${this.apiURL}/map/${uniqueId}`);
  }
  getAdditionalNotes(uniqueId: number): Observable<any> {
    return this.restClient.get<any>(
      api`${this.detailsApiUrl}/additional-notes/${uniqueId}`,
    );
  }
  getTankNotes(uniqueId: number): Observable<any> {
    return this.restClient.get<any>(
      api`${this.detailsApiUrl}/tank-notes/${uniqueId}`,
    );
  }
  getRtuNotes(uniqueId: number): Observable<any> {
    return this.restClient.get<any>(
      api`${this.detailsApiUrl}/rtu-notes/${uniqueId}`,
    );
  }
  getMapDataByUniqueId(uniqueId: number): Observable<any> {
    return this.restClient.get<any>(api`${this.apiURL}/map-info/${uniqueId}`);
  }
  getKeyDates(uniqueId: number): Observable<KeyDatesChartVM[] | null> {
    return this.restClient
      .post(api`${this.detailsApiUrl}/getkeydates`, uniqueId)
      .pipe(
        map((response) => {
          let keyDatesResponse: KeyDatesChartResponse = response;
          const todaysDate = keyDatesResponse.trendLineData.find(
            (data) => data.keyDateType === KeyDateTypes.TodaysDate,
          )?.date;

          let x = keyDatesResponse.trendLineData.map((dataPoint) => {
            return {
              date: dataPoint.date,
              volume: dataPoint.volume,
              color:
                dataPoint.date < todaysDate
                  ? ChartColors.yellow
                  : ChartColors.red,
              dashArray: this.getKeyToKeyDatesDashArray(dataPoint.keyDateType),
              legendText: this.getKeyToKeyDatesLegendText(
                dataPoint.keyDateType,
              ),
              striplineColor:
                dataPoint.date < todaysDate
                  ? ChartColors.yellow
                  : dataPoint.date === todaysDate
                    ? ChartColors.green
                    : ChartColors.red,
              keyDateType: dataPoint.keyDateType,
            };
          });

          return x;
        }),
      );
  }

  exportTankInformationToExcel(data: any) {
    if (!data) return;

    this.restClient
      .postFile(api`${this.detailsApiUrl}/ExportTankDetailsToExcel`, data)
      .subscribe((response) => {
        let fileName = 'TankInfo.xlsx';
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        new FileUtilities().downloadCSV(fileName, url);
      });
  }

  /** @deprecated Legacy Audit History; replaced by TankSetupService.exportTankChangeHistory behind the tankChangeHistory feature flag. */
  exportCompareConfigsToExcel(data: any) {
    if (!data) return;
    this.restClient
      .postFile(api`${this.detailsApiUrl}/ExportCompareConfigsToExcel`, data)
      .subscribe((response) => {
        let fileName = 'CompareConfigs.xlsx';
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        new FileUtilities().downloadCSV(fileName, url);
      });
  }

  estimatedUsageInfo$: BehaviorSubject<IInfoPanelDataItem[]> =
    new BehaviorSubject<IInfoPanelDataItem[]>(null);
  getEstimatedUsageInfo(
    tankId: number,
    dateRange$: Observable<IDateRange>,
  ): Observable<IInfoPanelDataItem[]> {
    const model = [
      {
        header: 'TankHistory.TimeSpan',
        formattedValue: null,
      },
      {
        header: 'TankHistory.ActualDateRange',
        formattedValue: null,
      },
      {
        header: 'TankHistory.TotalActiveUsage',
        formattedValue: null,
      },
      {
        header: 'TankHistory.TotalInactiveUsage',
        formattedValue: null,
      },
      {
        header: 'TankHistory.AvgDailyUsage',
        formattedValue: null,
      },
      {
        header: 'TankHistory.MaxDailyUsage',
        formattedValue: null,
      },
      {
        header: 'TankHistory.DaysToAlarm',
        formattedValue: null,
      },
      {
        header: 'TankHistory.DaysToCritical',
        formattedValue: null,
      },
      {
        header: 'TankHistory.DaysToLimit',
        formattedValue: null,
      },
    ];

    if (this.estimatedUsageInfo$ && !this.estimatedUsageInfo$.closed)
      this.estimatedUsageInfo$.next(model);

    dateRange$
      .pipe(
        switchMap((dateRange) => {
          return this.restClient
            .getDirect<ITankHistoryEstimatedUsageInfo>(
              api`${this.apiURL
              }/${tankId}/history-usage/estimated-usage-information?startDate=${part(dateRange.startDate.toDateString())}&endDate=${part(dateRange.endDate.toDateString())}&adft=${dateRange.activeDaysFilterType
              }`,
            )
            .pipe(
              map((data) => {
                const rtn: IInfoPanelDataItem[] = [];
                if (!data) return rtn;
                rtn.push({
                  header: 'TankHistory.TimeSpan',
                  formattedValue: `${data.timeSpan} ${data.daysLabel}`,
                });
                rtn.push({
                  header: 'TankHistory.ActualDateRange',
                  formattedValue: `${data.actualDateBegin} - ${data.actualDateEnd}`,
                });
                rtn.push({
                  header: 'TankHistory.TotalActiveUsage',
                  formattedValue: `${data.totalActiveUsage} ${data.volumeUoM}`,
                });
                rtn.push({
                  header: 'TankHistory.TotalInactiveUsage',
                  formattedValue: `${data.totalInactiveUsage} ${data.volumeUoM}`,
                });
                rtn.push({
                  header: 'TankHistory.AvgDailyUsage',
                  formattedValue: `${data.avgDailyUsage} ${data.volumeUoM}`,
                });
                rtn.push({
                  header: 'TankHistory.MaxDailyUsage',
                  formattedValue: `${data.maxDailyUsage} ${data.volumeUoM}`,
                });
                rtn.push({
                  header: 'TankHistory.DaysToAlarm',
                  formattedValue: `${data.daysToAlarm} ${data.daysLabel}`,
                });
                rtn.push({
                  header: 'TankHistory.DaysToCritical',
                  formattedValue: `${data.daysToCritical} ${data.daysLabel}`,
                });
                rtn.push({
                  header: 'TankHistory.DaysToLimit',
                  formattedValue: `${data.daysToLimit} ${data.daysLabel}`,
                });

                return rtn;
              }),
            );
        }),
      )
      .subscribe((data) => {
        this.estimatedUsageInfo$.next(data);
      });
    return this.estimatedUsageInfo$.asObservable();
  }

  estimatedShipmentInfo$: BehaviorSubject<IInfoPanelDataItem[]> =
    new BehaviorSubject<IInfoPanelDataItem[]>(null);
  getEstimatedShipmentInfo(
    tankId: number,
    dateRange$: Observable<IDateRange>,
  ): Observable<IInfoPanelDataItem[]> {
    const model = [
      {
        header: 'TankHistory.NumberOfFills',
        formattedValue: null,
      },
      {
        header: 'TankHistory.LastDrainDate',
        formattedValue: null,
      },
      {
        header: 'TankHistory.LastDrainAmount',
        formattedValue: null,
      },
      {
        header: 'TankHistory.TotalDrainAmount',
        formattedValue: null,
      },
      {
        header: 'TankHistory.AvgDrainAmount',
        formattedValue: null,
      },
      {
        header: 'TankHistory.MaxDrainAmount',
        formattedValue: null,
      },
      {
        header: 'TankHistory.AvgTimeBetweenFills',
        formattedValue: null,
      },
      {
        header: 'TankHistory.AvgInventory',
        formattedValue: null,
      },
    ];

    if (this.estimatedShipmentInfo$ && !this.estimatedShipmentInfo$.closed)
      this.estimatedShipmentInfo$.next(model);

    dateRange$
      .pipe(
        switchMap((dateRange) => {
          return this.restClient
            .getDirect<ITankHistoryEstimatedShipmentInfo>(
              api`${this.apiURL
              }/${tankId}/history-usage/estimated-shipment-information?startDate=${part(dateRange.startDate.toISOString())}&endDate=${part(dateRange.endDate.toISOString())}&adft=${dateRange.activeDaysFilterType
              }`,
            )
            .pipe(
              map((data) => {
                const rtn: IInfoPanelDataItem[] = [];
                if (!data) return rtn;
                let fillOrDrain = data.IsFillTank ? 'Fill' : 'Drain';
                let fillsOrDrains = data.IsFillTank ? 'Fills' : 'Drains';
                rtn.push({
                  header: `TankHistory.NumberOf${fillsOrDrains}`,
                  formattedValue: `${data.numberOfFillsOrDrains}`,
                });
                rtn.push({
                  header: `TankHistory.Last${fillOrDrain}Date`,
                  formattedValue: `${data.lastFillOrDrainDate}`,
                });
                rtn.push({
                  header: `TankHistory.Last${fillOrDrain}Amount`,
                  formattedValue: `${data.lastFillOrDrainAmount} ${data.volumeUoM}`,
                });
                rtn.push({
                  header: `TankHistory.Total${fillOrDrain}Amount`,
                  formattedValue: `${data.totalFillOrDrainAmount} ${data.volumeUoM}`,
                });
                rtn.push({
                  header: `TankHistory.Avg${fillOrDrain}Amount`,
                  formattedValue: `${data.avgFillOrDrainAmount} ${data.volumeUoM}`,
                });
                rtn.push({
                  header: `TankHistory.Max${fillOrDrain}Amount`,
                  formattedValue: `${data.maxFillOrDrainAmount} ${data.volumeUoM}`,
                });
                rtn.push({
                  header: `TankHistory.AvgTimeBetween${fillsOrDrains}`,
                  formattedValue: `${data.avgTimeBetweenFillsOrDrains} ${data.daysLabel}`,
                });
                rtn.push({
                  header: 'TankHistory.AvgInventory',
                  formattedValue: `${data.avgInventory} ${data.volumeUoM}`,
                });

                return rtn;
              }),
            );
        }),
      )
      .subscribe((data) => {
        this.estimatedShipmentInfo$.next(data);
      });
    return this.estimatedShipmentInfo$.asObservable();
  }

  historicalInventoryInfo$: BehaviorSubject<{
    data: IInfoPanelDataItem[];
    activeDaysMask: number;
  }> = new BehaviorSubject<{
    data: IInfoPanelDataItem[];
    activeDaysMask: number;
  }>(null);
  getHistoricalInventoryInfo(
    tankId: number,
  ): Observable<{ data: IInfoPanelDataItem[]; activeDaysMask: number }> {
    const model = {
      data: [
        {
          header: 'TankHistory.AvailableCapacity',
          formattedValue: null,
        },
        {
          header: 'TankHistory.Status',
          formattedValue: null,
        },
        {
          header: 'TankHistory.Inventory',
          formattedValue: null,
        },
        {
          header: 'TankHistory.InventoryTime',
          formattedValue: null,
        },
        {
          header: 'TankHistory.ActiveDays',
          formattedValue: null,
        },
      ],
      activeDaysMask: 0,
    };

    if (this.historicalInventoryInfo$ && !this.historicalInventoryInfo$.closed)
      this.historicalInventoryInfo$.next(model);

    this.restClient
      .getDirect<ITankHistoryInventoryInfo>(
        api`${this.apiURL}/${tankId}/history-usage/inventory-information`,
      )
      .pipe(
        map((data) => {
          let rtnDataItems: IInfoPanelDataItem[] = [];
          let rtn = {
            data: rtnDataItems,
            activeDaysMask: data.activeDaysMask,
          };

          if (!data) return rtn;

          rtnDataItems.push({
            header: 'TankHistory.AvailableCapacity',
            formattedValue: `${data.availableCapacityConvertedValue} ${data.volumeUoM}`,
          });
          rtnDataItems.push({
            header: 'TankHistory.Status',
            formattedValue: `${data.status}`,
          });
          rtnDataItems.push({
            header: 'TankHistory.Inventory',
            formattedValue: `${data.inventoryVolumeConvertedValue} ${data.volumeUoM}`,
          });
          rtnDataItems.push({
            header: 'TankHistory.InventoryTime',
            formattedValue: `${new Date(
              data.inventoryTime,
            ).toLocaleDateString()} ${new Date(
              data.inventoryTime,
            ).toLocaleTimeString()}`,
          });
          rtnDataItems.push({
            header: 'TankHistory.ActiveDays',
            formattedValue: `${data.activeDays}`,
          });

          return rtn;
        }),
      )
      .subscribe((data) => {
        this.historicalInventoryInfo$.next(data);
      });
    return this.historicalInventoryInfo$.asObservable();
  }

  historicalAndUsageInformation$: BehaviorSubject<any> =
    new BehaviorSubject<any>(null);
  getHistoricalAndUsageInformation(
    tankId: number,
  ): Observable<IInfoPanelDataItem[]> {
    const model = [
      {
        header: 'TankHistory.Capacity',
        formattedValue: null,
      },
      {
        header: 'TankHistory.LowAlarm',
        formattedValue: null,
      },
      {
        header: 'TankHistory.CriticalLowAlarm',
        formattedValue: null,
      },
      {
        header: 'TankHistory.Level',
        formattedValue: null,
      },
      {
        header: 'TankHistory.TansportationStatus',
        formattedValue: null,
      },
    ];

    if (
      this.historicalAndUsageInformation$ &&
      !this.historicalAndUsageInformation$.closed
    )
      this.historicalAndUsageInformation$.next(model);

    //pipe the date range observable into the API call
    // any changes to the date range will trigger a new API call.
    combineLatest(
      [
        this.restClient.getDirect<ITankHistoryUsageInfo>(
          api`${this.apiURL}/${tankId}/history-usage/information`,
        ),
        this.store.select(selectUserInformation).pipe(take(1)),
      ],
      (data, userInfo) => {
        const rtn: IInfoPanelDataItem[] = [];
        rtn.push({
          header: 'TankHistory.Capacity',
          formattedValue: `${formatNumber(
            data.capacity,
            userInfo.cultureCode,
            '1.2-2',
          )} ${data.volumeUoM}`,
        });
        rtn.push({
          header: 'TankHistory.LowAlarm',
          formattedValue: `${data.lowAlarm} %`,
        });
        rtn.push({
          header: 'TankHistory.CriticalLowAlarm',
          formattedValue: `${data.critialLowAlarm} %`,
        });
        rtn.push({
          header: 'TankHistory.Level',
          formattedValue: `${data.grossLevel} ${data.levelUoM}`,
        });
        rtn.push({
          header: 'TankHistory.TansportationStatus',
          formattedValue: data.transportationStatus,
        });
        return rtn;
      },
    ).subscribe((data) => {
      this.historicalAndUsageInformation$.next(data);
    });
    return this.historicalAndUsageInformation$.asObservable();
  }

  getHistoricalDailyUsage(
    tankId: number,
    dateRange$: Observable<IDateRange>,
  ): Observable<ITankAggregatedUsageChartVM> {
    //pipe the date range observable into the API call
    // any changes to the date range will trigger a new API call.
    return dateRange$.pipe(
      switchMap((dateRange) => {
        return this.restClient.getDirect<ITankAggregatedUsageChartVM>(
          api`${this.apiURL
          }/${tankId}/history-usage/daily-usage?startDate=${part(dateRange.startDate.toDateString())}&endDate=${part(dateRange.endDate.toDateString())}&adft=${dateRange.activeDaysFilterType
          }`,
        );
      }),
    );
  }

  getHistoricalVolumeUsage(
    tankId: number,
    dateRange$: Observable<IDateRange>,
  ): Observable<any> {
    //pipe the date range observable into the API call
    // any changes to the date range will trigger a new API call.
    return dateRange$.pipe(
      switchMap((dateRange) => {
        return this.restClient.getDirect<any>(
          api`${this.apiURL
          }/${tankId}/history-usage/volume-usage?startDate=${part(dateRange.startDate.toISOString())}&endDate=${part(dateRange.endDate.toISOString())}&adft=${dateRange.activeDaysFilterType
          }`,
        );
      }),

      //Format a tooltip for each data point
      map((data) => {
        data.dataPoints = data.dataPoints.map((dp: ITankVolumeDataPoint) => {
          const convDate = new Date(dp.invTime);
          return {
            ...dp,
            tooltip: Intl.DateTimeFormat(undefined, {
              day: 'numeric',
              month: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
            }).format(convDate),
          };
        });
        return data;
      }),
    );
  }

  private getKeyToKeyDatesLegendText(keyDateType?: KeyDateTypes): any {
    switch (keyDateType) {
      case KeyDateTypes.DateToOrder:
        return 'Charting.Legend.DatetoOrder';
      case KeyDateTypes.DateToSafetyStock:
        return 'Charting.Legend.DatetoSafetyStock';
      case KeyDateTypes.DateToEmpty:
        return 'Charting.Legend.DatetoEmpty';
      case KeyDateTypes.TodaysDate:
        return 'Charting.Legend.TodaysDate';
      default:
        return null;
    }
  }

  private getKeyToKeyDatesDashArray(keyDateType?: KeyDateTypes): any {
    switch (keyDateType) {
      case KeyDateTypes.DateToOrder:
        return '5,1';
      case KeyDateTypes.DateToSafetyStock:
        return '5,3';
      case KeyDateTypes.DateToEmpty:
        return '5,8';
      case KeyDateTypes.TodaysDate:
      default:
        return null;
    }
  }

  getHistoricalInventoryRecordFilter(
    uniqueId: number,
    dateRange$: Observable<IDateRange>,
  ): Observable<any> {
    //pipe the date range observable into the API call
    // any changes to the date range will trigger a new API call.
    return dateRange$.pipe(
      map((dateRange) => {
        return {
          dateRange: dateRange,
          uniqueId: uniqueId,
          showInactiveRecords: true,
        };
      }),
    );
  }

  getHistoricalInventoryRecords(payload: any): Observable<any> {
    let request = {
      uniqueId: payload.filterModel.uniqueId,
      startDate: payload.filterModel.dateRange.startDate.toDateString(),
      endDate: payload.filterModel.dateRange.endDate.toDateString(),
      startRow: payload.startRow,
      endRow: payload.endRow,
      showInactiveRecords: payload.filterModel.showInactiveRecords,
      activeDaysFilterType: payload.filterModel.dateRange.activeDaysFilterType,
    };

    let apiResponse = this.restClient.post(
      api`${this.apiURL}/GetHistoricalInventoryRecords`,
      request,
    );
    return apiResponse;
    //  let filterModel = payload.filterModel;
    //  return this.restClient.getDirect<any>(`${this.apiURL}/${filterModel.tankId}/history-usage/inventory-records?startDate=${filterModel.dateRange.startDate.toISOString()}&endDate=${filterModel.dateRange.endDate.toISOString()}`);
  }

  getNextOrPrevTankx(params: any): Observable<any> {
    let apiResponse = this.restClient.post(
      api`${this.apiURL}/getNextOrPrevTank`,
      params,
    );
    return apiResponse.pipe(
      tap((response) => {
        this.logger.log('Tank/getNextOrPrevTank Response', response);
      }),
    );
    return apiResponse;
    //  let filterModel = payload.filterModel;
    //  return this.restClient.getDirect<any>(`${this.apiURL}/${filterModel.tankId}/history-usage/inventory-records?startDate=${filterModel.dateRange.startDate.toISOString()}&endDate=${filterModel.dateRange.endDate.toISOString()}`);
  }

  isAffiliatedTank(): boolean {
    let tank = this.getKeyTankInfoDirectFromCache();
    if (!tank) {
      return false;
    }

    return [1, true].includes(tank.isAffiliatedTank);
  }

  isReplicateTank(): boolean {
    const tank = this.getKeyTankInfoDirectFromCache();
    if (!tank) return;

    return tank.isReplicateTank;
  }

  exportHistoricalInventoryRecords(payload: any) {
    if (!payload) return;

    let request = {
      uniqueId: payload.uniqueId,
      startDate: payload.dateRange.startDate.toISOString(),
      endDate: payload.dateRange.endDate.toISOString(),
      startRow: 0,
      endRow: 0,
      showInactiveRecords: payload.showInactiveRecords,
      columnOptions: payload.columnOptions,
    };

    this.restClient
      .postFile(
        api`${this.apiURL}/ExportHistoricalInventoryRecordsToExcel`,
        request,
      )
      .subscribe((response) => {
        let fileName = 'TankInventoryHistory.xlsx';
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        new FileUtilities().downloadCSV(fileName, url);
      });
  }

  // Cached: TankAccessGuard calls this on every navigation into /tank/:id, and the answer only
  // changes with the user's org, which busts the global cache on login/logout.
  @Cacheable()
  validateCSOIsTankOrg(uniqueId: number): Observable<number> {
    //call the API to validate if the tank is in the same org as the CSO
    const url = api`${this.apiURL}/validate-cso-is-tank-org/${uniqueId}`;
    return this.restClient.getDirect<number>(url).pipe(
      tap((response) => {
        this.logger.log(url, response);
      }),
    );
  }

  getTanksForDropDownByLocationId(locationId: number): Observable<any> {
    //call the API to validate if the tank is in the same org as the CSO
    const url = api`${this.apiURL}/get-dropdown-by-loc/${locationId}`;
    return this.restClient.getDirect<any>(url).pipe(
      tap((response) => {
        this.logger.log(url, response);
      }),
    );
  }

  getTanksForDropDownByLocationIds(
    locationIds: number[],
  ): Observable<any> {
    if (!Array.isArray(locationIds) || locationIds.length === 0) return of([]);
  
    const url = api`${this.apiURL}/get-tanks-by-locIds`;
    return this.restClient.postDirect<any>(url, locationIds).pipe(
      tap((response) => {
        this.logger.log(url, response);
      })
    );
  }

  

  addNotes(noteMessage: string, uniqueId: number, noteType: string): Observable<any> {
    const url = api`${this.detailsApiUrl}/add-notes/${uniqueId}/${part(noteType)}`;
    const payload = {
      note1: noteMessage,
      superUserOnly: false
    };
    return this.restClient.post(url, payload).pipe(
      tap((response) => {
        this.logger.log(url, response);
      }),
    );
  }

  updateNotes(payload: any): Observable<any> {
    const url = api`${this.detailsApiUrl}/update-notes`;
    return this.restClient.post(url, payload).pipe(
      tap((response) => {
        this.logger.log(url, response);
      }),
    );
  }

  deleteNotes(noteId: number): Observable<any> {
    const url = api`${this.detailsApiUrl}/delete-notes/${noteId}`;
    return this.restClient.post(url, null).pipe(
      tap((response) => {
        this.logger.log(url, response);
      }),
    );
  }

  getManualInventoryInfo(uniqueId: number): Observable<any> {
    const url = api`${this.apiURL}/${uniqueId}/history-usage/inventory-infoForManualEntry`;
    return this.restClient.getDirect(url);
  }

  getInventoryRecordStatus(tankId: number, invTime: any): Observable<any> {
    return this.restClient.getDirect<any>(
          api`${this.apiURL
          }/${tankId}/inventoryRecord-Processed?invTime=${part(invTime)}`,
        );
  }

  updateInventoryRecord(payload: any): Observable<any> {
    let apiResponse = this.restClient.post(
      api`${this.apiURL}/UpdateInventoryRecords`,
      payload,
    );
    return apiResponse.pipe(
      tap((response) => {
        this.logger.log('Tank/UpdateInventoryRecords Response', response);
      }),
    );
  }

  getUpdatedInventoryRecordsStatus(payload: any): Observable<any> {
    let apiResponse = this.restClient.post(
      api`${this.apiURL}/ValidateInventoryRecordsProcessed`,
      payload,
    );
    return apiResponse.pipe(
      tap((response) => {
        this.logger.log('Tank/ValidateInventoryRecordsProcessed Response', response);
      }),
    );
  }

  //=========================================================
  // Test the mobile app APIs
  //=========================================================
  //rtuLookup(): any {
  //  // common code for all the API test calls
  //  const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'MOBILE_TANK_INSTALLER_APP_API_KEY': "5b240cf1-cvcz-3151-lwg6-52a7c65f69fd" });
  //  const options = { headers: headers };
  //  const url2 = `/api/v1/tank-installer/rtulookup/910043410`;
  //  var result2 = this._httpClient.get<any>(url2, options).subscribe((data) => {
  //    debugger;
  //  });
  //}

  //rtuLookupV2(): any {
  //  // common code for all the API test calls
  //  const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'MOBILE_TANK_INSTALLER_APP_API_KEY': "5b240cf1-cvcz-3151-lwg6-52a7c65f69fd" });
  //  const options = { headers: headers };
  //  const url2 = `/api/v1/tank-installer/rtulookup/v2/910043410`;
  //  var result2 = this._httpClient.get<any>(url2, options).subscribe((data) => {
  //    debugger;
  //  });
  //}


  //testMobileAppTankInstallerAPIs(): void {

  //var result = this.restClient.get<any>(`tank-setup/102387320/GetUnitOfMeasureListByTelemetryType`).subscribe((data) => {
  //});

  //    //const url = `/api/v1/tank-installer/getPasswordStrengthRulesByOrgId/2136`;
  //    //var result = this.restClient.get<any>(`tank-installer/rtuLookup/707431444`).subscribe((data) => {
  //    //});

  //    //const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'MOBILE_TANK_INSTALLER_APP_API_KEY': "" });
  //    //const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'MOBILE_TANK_INSTALLER_APP_API_KEY': "5b240cf1" });
  //    //const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'MOBILE_TANK_INSTALLER_APP_API_KEY': "52a7c65f69fd" });
  //    //const options = { headers: headers };
  //    //const url = `/api/v1/tank-installer/getPasswordStrengthRulesByOrgId/2136`;
  //    //var result = this._httpClient.get<any>(url, options).subscribe((data) => {
  //    //});

  //    //// test the CreateNewUser API
  //    //request = {
  //    //  billableOrgId: 642,
  //    //  firstName: "greg",
  //    //  lastName: "test",
  //    //  userName: "gregtest1",
  //    //  password: "TestPassword1!",
  //    //  emailAddress: "test@bademail.com",
  //    //  languagePref: 1
  //    //};
  //    //const url = `/api/v1/tank-installer/CreateNewUser`;

  //    //// test the UOM list API
  //    //const url = `/api/v1/tank-setup/GetUnitOfMeasureListByTelemetryType`;

  //    //    // test the user login API
  //    //    const url = `/api/v1/tank-installer/MobileTankInstallerLogin`;
  //    //    request = { userName: "gregtest1", password: "TestPassword1!", billableOrgId: 642 };

  //     //test the mobile AssignHostDevice API
  //    const url = `/api/v1/tank/AssignHostDevice`;
  //    request = { tankId: "10184177", hostDeviceId: "123456"};

  //    // common code for all the API test calls
  //    const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'MOBILE_TANK_INSTALLER_APP_API_KEY': "5b240cf1-cvcz-3151-lwg6-52a7c65f69fd" });
  //    const options = { headers: headers };

  //    var result = this._httpClient.post(url, request, options).subscribe((data) => {
  //    });
  //  }
}
